import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { docs } from '@services/docs/docs-service'
import { DocumentStatus } from '@interfaces/document'
import { template } from '@services/template/template-service'
import LoadingIndicator from '@/components/ui/loading-indicator'
import { eventsService } from '@/services/analytics/events-service'
import { EventCategory, EventName } from '@interfaces/events'
import { user as userService } from '@services/user/user-service'
import { toast } from 'react-hot-toast'
import { useBilling } from '@/hooks/useBilling'

export default function EditorPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { onSubscribe } = useBilling()

  const fetchStatusByPolling = async (fileId: string) => {
    const response = await docs.getStatusByPublicId(fileId)
    if (response.status === 200) {
      const { status } = response.data as DocumentStatus
      if (status !== 'processing') {
        return true
      }
    }
    return false
  }

  useEffect(() => {
    const templateId = searchParams.get('templateId')

    const processTemplate = async () => {
      try {
        const response = await template.processTemplate(templateId)

        if (response.status === 200) {
          const publicId = response.data.public_id

          if (templateId) {
            // fire template_opened event
            eventsService.fireEvent({
              name: EventName.TEMPLATE_OPENED,
              category: EventCategory.CORE_BEHAVIOR,
              doc_public_id: publicId,
              source: undefined,
              meta: {},
            })
          } else {
            // fire BLANK_DOC event
            eventsService.fireEvent({
              name: EventName.BLANK_DOC,
              category: EventCategory.CORE_BEHAVIOR,
              doc_public_id: publicId,
              source: undefined,
              meta: {},
            })
          }

          // Start polling for document status
          const intervalId = setInterval(async () => {
            const isDataReady = await fetchStatusByPolling(publicId)
            if (isDataReady) {
              clearInterval(intervalId)

              // send email sequence
              userService.sendEmail('email-sequence-website', publicId)

              // Replace the current history entry with the editor page
              navigate(`/review/${publicId}`, { replace: true })
            }
          }, 2000)

          return () => clearInterval(intervalId)
        }
      } catch (err: any) {
        const errorCode = err.response?.data?.errorCode

        if (errorCode) {
          onSubscribe(errorCode)
        } else {
          toast.error('Sorry, we could not find the requested template.')
        }

        navigate(`/dashboard`, { replace: true })
      }
    }

    processTemplate()
  }, [searchParams, navigate])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <LoadingIndicator label="Opening Document" />
    </div>
  )
}
